import { ApiClient } from './api-client';
import type { ConversationDetail } from './chat.service';
import type { ApiResponse } from '../types';

export type RequestStatus = 'ACTIVE' | 'RESOLVED' | 'EXPIRED';

/** Allowed values come from {@code RequestsService.ALLOWED_DURATIONS} on the
 *  server. UI presets must match this set. */
export const REQUEST_DURATIONS = [3, 7, 14, 30] as const;
export type RequestDurationDays = (typeof REQUEST_DURATIONS)[number];
export const DEFAULT_REQUEST_DURATION: RequestDurationDays = 7;

export interface RequestOwnerRef {
  /** Null when the request was posted anonymously. */
  userId: string | null;
  /** Null when anonymous; otherwise the registered username. */
  username: string | null;
  /** Always set — falls back to the captured owner_name for anonymous posts. */
  displayName: string | null;
}

/** Contact info for an anonymously-posted request. Revealed only to
 *  authenticated viewers; null otherwise (and null for posts that have a
 *  registered owner — those use the chat instead). */
export interface RequestOwnerContact {
  email: string;
  phone: string | null;
}

export interface CommunityRequest {
  id: string;
  title: string;
  description: string | null;
  imageUrl: string | null;
  cityId: string | null;
  cityName: string | null;
  countryCode: string | null;
  status: RequestStatus;
  /** ISO-8601 instant. */
  expiresAt: string;
  resolvedAt: string | null;
  createdAt: string;
  owner: RequestOwnerRef;
  /** Contact info for anonymous posts, visible to authenticated viewers only. */
  ownerContact: RequestOwnerContact | null;
  /** True when the viewer owns this request — drives "edit / resolve" CTAs. */
  isOwner: boolean;
  /** Number of distinct responders who have already opened a chat thread. */
  responseCount: number;
}

export interface CreateRequestPayload {
  title: string;
  description?: string;
  imageUrl?: string;
  /** Free-text city name — backend stores it on the request and uses it for the public-feed filter. */
  cityName?: string;
  /** ISO-3166-1 alpha-2. */
  countryCode?: string;
  durationDays: RequestDurationDays;
  /** Anonymous-post fields — ignored when the caller is authenticated.
   *  Backend requires {@code ownerName} and {@code ownerEmail} when unauth. */
  ownerName?: string;
  ownerEmail?: string;
  ownerPhone?: string;
}

export interface UpdateRequestPayload {
  title: string;
  description?: string;
  imageUrl?: string;
  cityName?: string;
  countryCode?: string;
}

export interface RequestListQuery {
  /** Case-insensitive substring match on the stored city name. */
  cityName?: string;
  countryCode?: string;
  page?: number;
  size?: number;
}

export const requestsService = {
  // ── Public ──────────────────────────────────────────────────────────
  listPublic: (params: RequestListQuery = {}): Promise<ApiResponse<CommunityRequest[]>> =>
    ApiClient.get<CommunityRequest[], RequestListQuery>('/community-requests', params),

  get: async (id: string): Promise<CommunityRequest> =>
    (await ApiClient.get<CommunityRequest>(`/community-requests/${id}`)).data,

  // ── Owner ───────────────────────────────────────────────────────────
  create: async (payload: CreateRequestPayload): Promise<CommunityRequest> =>
    (await ApiClient.post<CommunityRequest>('/community-requests', payload)).data,

  update: async (id: string, payload: UpdateRequestPayload): Promise<CommunityRequest> =>
    (await ApiClient.put<CommunityRequest>(`/community-requests/${id}`, payload)).data,

  resolve: async (id: string): Promise<CommunityRequest> =>
    (await ApiClient.post<CommunityRequest>(`/community-requests/${id}/resolve`)).data,

  delete: async (id: string): Promise<void> => {
    await ApiClient.delete(`/community-requests/${id}`);
  },

  listMine: (params: { page?: number; size?: number } = {}): Promise<ApiResponse<CommunityRequest[]>> =>
    ApiClient.get<CommunityRequest[], { page?: number; size?: number }>(
      '/me/community-requests',
      params,
    ),

  // ── Chat integration ───────────────────────────────────────────────
  /** Open (or reuse) the private chat thread with the request's owner.
   *  Optional initial message is posted in the same round-trip. */
  respond: async (id: string, body?: string): Promise<ConversationDetail> =>
    (
      await ApiClient.post<ConversationDetail>(
        `/community-requests/${id}/respond`,
        body ? { message: body } : {},
      )
    ).data,
};
